import { Globe, X, Sun, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

import { useAuth } from '../../providers/AuthProvider';
import {
  addCountryBorders,
  applyCloudLayer,
  applyEarthSurface,
  createAtmosphere,
  loadNightLights,
} from '../globe/scenery';

// View modes for the globe
const VIEW_MODES = {
  DAY: 'day',
  NIGHT: 'night',
  WINTER: 'winter',
  INFRARED: 'infrared',
  REALISTIC: 'realistic',
  NASA_LIVE: 'nasa_live',
} as const;

type ViewMode = (typeof VIEW_MODES)[keyof typeof VIEW_MODES];

export interface MarkerData {
  id: string;
  name: string;
  lat: number;
  lon: number;
  type: 'organization' | 'location';
  status?: string;
  plan?: string;
  /** When set (Kokpit globu, F.3) the pin color encodes HEALTH, not status. */
  health?: 'ok' | 'warn' | 'critical';
}

interface HoveredItem {
  type: string;
  data: MarkerData;
}

// Helper function to convert lat/lon to 3D position
const latLonToVector3 = (lat: number, lon: number, radius: number): THREE.Vector3 => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);
  return new THREE.Vector3(x, y, z);
};

// Normalize Polish address by removing/expanding common prefixes
// This helps Nominatim geocoder work better with Polish addresses
const normalizePolishAddress = (address: string): string => {
  if (!address) return address;

  // Common Polish address prefixes - we remove them as Nominatim works better without them
  const prefixPatterns = [
    /^ul\.\s*/i, // ul. (ulica - street)
    /^ulica\s+/i, // ulica
    /^al\.\s*/i, // al. (aleja - avenue)
    /^aleja\s+/i, // aleja
    /^os\.\s*/i, // os. (osiedle - housing estate)
    /^osiedle\s+/i, // osiedle
    /^pl\.\s*/i, // pl. (plac - square)
    /^plac\s+/i, // plac
    /^rondo\s+/i, // rondo (roundabout)
    /^skwer\s+/i, // skwer (small square)
    /^bulwar\s+/i, // bulwar (boulevard)
    /^wybrzeże\s+/i, // wybrzeże (waterfront)
    /^park\s+/i, // park
  ];

  let normalized = address.trim();
  for (const pattern of prefixPatterns) {
    normalized = normalized.replace(pattern, '');
  }
  return normalized.trim();
};

// Polish city coordinates lookup for geocoding fallback (outside component to avoid re-renders)
// Exported so CockpitPage (F.3) can place tenant pins without re-geocoding.
export const CITY_COORDINATES: Record<string, { lat: number; lon: number }> = {
  warszawa: { lat: 52.2297, lon: 21.0122 },
  warsaw: { lat: 52.2297, lon: 21.0122 },
  kraków: { lat: 50.0647, lon: 19.945 },
  krakow: { lat: 50.0647, lon: 19.945 },
  gdańsk: { lat: 54.352, lon: 18.6466 },
  gdansk: { lat: 54.352, lon: 18.6466 },
  gdynia: { lat: 54.5189, lon: 18.5305 },
  sopot: { lat: 54.4418, lon: 18.5601 },
  poznań: { lat: 52.4064, lon: 16.9252 },
  poznan: { lat: 52.4064, lon: 16.9252 },
  wrocław: { lat: 51.1079, lon: 17.0385 },
  wroclaw: { lat: 51.1079, lon: 17.0385 },
  łódź: { lat: 51.7592, lon: 19.456 },
  lodz: { lat: 51.7592, lon: 19.456 },
  szczecin: { lat: 53.4285, lon: 14.5528 },
  lublin: { lat: 51.2465, lon: 22.5684 },
  słupsk: { lat: 54.4641, lon: 17.0285 },
  slupsk: { lat: 54.4641, lon: 17.0285 },
  lubliniec: { lat: 50.6683, lon: 18.6842 },
  koszalin: { lat: 54.1943, lon: 16.1714 },
  katowice: { lat: 50.2649, lon: 19.0238 },
  bydgoszcz: { lat: 53.1235, lon: 18.0084 },
  białystok: { lat: 53.1325, lon: 23.1688 },
  bialystok: { lat: 53.1325, lon: 23.1688 },
  toruń: { lat: 53.0138, lon: 18.5984 },
  torun: { lat: 53.0138, lon: 18.5984 },
  rzeszów: { lat: 50.0412, lon: 21.9991 },
  rzeszow: { lat: 50.0412, lon: 21.9991 },
  kielce: { lat: 50.8661, lon: 20.6286 },
  olsztyn: { lat: 53.7784, lon: 20.4801 },
  opole: { lat: 50.6751, lon: 17.9213 },
  'zielona góra': { lat: 51.9356, lon: 15.5062 },
  'zielona gora': { lat: 51.9356, lon: 15.5062 },
  'gorzów wielkopolski': { lat: 52.7368, lon: 15.2288 },
  gorzow: { lat: 52.7368, lon: 15.2288 },
};

// Calculate real sun position based on current time
const calculateSunPosition = (date: Date): { lat: number; lon: number } => {
  const JD = date.getTime() / 86400000 + 2440587.5;
  const T = (JD - 2451545.0) / 36525.0;
  const L0 = (280.46646 + 36000.76983 * T + 0.0003032 * T * T) % 360;
  const M = (357.52911 + 35999.05029 * T - 0.0001537 * T * T) % 360;
  const M_rad = (M * Math.PI) / 180;
  const C =
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M_rad) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * M_rad) +
    0.000289 * Math.sin(3 * M_rad);
  const L = (L0 + C) % 360;
  const L_rad = (L * Math.PI) / 180;
  const epsilon = 23.439 - 0.0000004 * T;
  const epsilon_rad = (epsilon * Math.PI) / 180;
  const declination = (Math.asin(Math.sin(epsilon_rad) * Math.sin(L_rad)) * 180) / Math.PI;
  const utcHours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  const E = 4 * (M - L0 + C);
  const solarTime = utcHours + E / 60;
  const longitude = (solarTime - 12) * 15;
  return { lat: declination, lon: longitude };
};

export interface EarthDashboardProps {
  /**
   * Kokpit globu (F.3): when provided, these pins replace the default
   * "geocode the current organization" behaviour — the caller supplies one
   * marker per tenant (color = health via MarkerData.health).
   */
  markersOverride?: MarkerData[];
}

export function EarthDashboard({ markersOverride }: EarthDashboardProps = {}) {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const globeRef = useRef<THREE.Mesh | null>(null);
  const sunLightRef = useRef<THREE.DirectionalLight | null>(null);
  const sunIndicatorRef = useRef<THREE.Mesh | null>(null);
  const sunMeshRef = useRef<THREE.Mesh | null>(null);
  const sunGlowRef = useRef<THREE.Sprite | null>(null);
  const cloudsRef = useRef<THREE.Mesh | null>(null);
  const starsRef = useRef<THREE.Points | null>(null);
  const markersRef = useRef<THREE.Mesh[]>([]);
  const animationIdRef = useRef<number | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const mouseWorldRef = useRef({ x: 0, y: 0 });
  const locationAnimationRef = useRef<{
    active: boolean;
    target: THREE.Vector3 | null;
    startPos: THREE.Vector3 | null;
    startZoom: number;
    progress: number;
  }>({ active: false, target: null, startPos: null, startZoom: 280, progress: 0 });

  // State
  const [selectedItem, setSelectedItem] = useState<HoveredItem | null>(null);
  const [hoveredItem, setHoveredItem] = useState<HoveredItem | null>(null);
  const [autoRotate, setAutoRotate] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Globe render style is fixed to the realistic Blue-Marble look. The old
  // "Space Control" overlay (view-mode switcher + sun tracking) was removed in
  // favour of a clean globe, so the mode no longer changes at runtime.
  const [viewMode] = useState<ViewMode>(VIEW_MODES.REALISTIC);
  const [transitionOpacity, setTransitionOpacity] = useState(1);
  // Bumped when an earth texture finishes loading so the view-mode effect
  // re-applies the active mode with the real maps in place.
  const [textureVersion, setTextureVersion] = useState(0);
  const nightTextureRef = useRef<THREE.Texture | null>(null);

  // Drag state refs
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const rotationSpeedRef = useRef({ x: 0, y: 0 });
  const targetZoomRef = useRef(280);
  const currentZoomRef = useRef(280);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const frameCountRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const autoRotateRef = useRef(false);
  // Sun-follow was removed (it kept snapping the camera back to the sun while
  // the user was dragging). Kept as a permanently-false ref so the animate loop
  // conditions below stay readable without a wider refactor.
  const followSunRef = useRef(false);
  // One-shot guard so the globe auto-centres on the user's location once.
  const hasAutoFocusedRef = useRef(false);

  // Pinch-to-zoom refs
  const isPinchingRef = useRef(false);
  const initialPinchDistanceRef = useRef(0);

  // State for geocoded markers
  const [markers, setMarkers] = useState<MarkerData[]>([]);

  // Geocode organization address and create marker data.
  // Override path (F.3): the cockpit supplies pre-built tenant pins directly.
  useEffect(() => {
    if (markersOverride) {
      setMarkers(markersOverride);
      return;
    }
    const geocodeOrganization = async () => {
      if (!currentOrganization) {
        setMarkers([]);
        return;
      }

      const org = currentOrganization.organization;

      // Use organization's coordinates if set
      let lat = org.latitude;
      let lon = org.longitude;

      // If no coordinates, try to geocode from full address
      if (lat == null || lon == null) {
        const { address, city, postal_code, voivodeship } = org;

        // Build address query for geocoding
        if (address || city) {
          try {
            const parts: string[] = [];
            // Normalize address to remove Polish prefixes that confuse Nominatim
            if (address) parts.push(normalizePolishAddress(address));
            if (city) parts.push(city);
            if (postal_code) parts.push(postal_code);
            if (voivodeship) parts.push(voivodeship);
            parts.push('Polska');

            const query = encodeURIComponent(parts.join(', '));
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=pl`,
              { headers: { 'Accept-Language': 'pl' } }
            );

            if (response.ok) {
              const data = await response.json();
              if (data && data.length > 0) {
                lat = parseFloat(data[0].lat);
                lon = parseFloat(data[0].lon);
              }
            }
          } catch (error) {
            console.warn('Geocoding failed:', error);
          }
        }

        // Fallback: try city lookup from constant
        if ((lat == null || lon == null) && city) {
          const cityKey = city.toLowerCase().trim();
          const cityCoords = CITY_COORDINATES[cityKey];
          if (cityCoords) {
            lat = cityCoords.lat;
            lon = cityCoords.lon;
          }
        }

        // Final fallback: Warsaw (Poland center)
        if (lat == null || lon == null) {
          lat = 52.2297;
          lon = 21.0122;
        }
      }

      setMarkers([
        {
          id: currentOrganization.organization_id,
          name: org.name,
          lat,
          lon,
          type: 'organization',
          status: org.status,
          plan: org.subscription_plan,
        },
      ]);
    };

    geocodeOrganization();
  }, [currentOrganization, markersOverride]);

  // Sync refs with state
  useEffect(() => {
    autoRotateRef.current = autoRotate;
  }, [autoRotate]);

  // Keep the day/night terminator lit by the real sun position. This only moves
  // the light — never the camera — so it can't fight the user dragging the globe.
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      const sunPos = calculateSunPosition(now);

      if (sunLightRef.current) {
        const phi = (90 - sunPos.lat) * (Math.PI / 180);
        const theta = sunPos.lon * (Math.PI / 180);
        const radius = 300;
        const x = radius * Math.sin(phi) * Math.cos(theta);
        const y = radius * Math.cos(phi);
        const z = radius * Math.sin(phi) * Math.sin(theta);
        sunLightRef.current.position.set(x, y, z);
        sunLightRef.current.lookAt(0, 0, 0);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Default view: gently fly the camera to the user's own location (the geocoded
  // organization marker) once, instead of leaving the globe parked over the
  // Atlantic at longitude 0. Runs a single time once the first marker exists.
  useEffect(() => {
    if (hasAutoFocusedRef.current) return;
    if (!markers.length || !cameraRef.current) return;
    const home = markers[0];
    if (!Number.isFinite(home.lat) || !Number.isFinite(home.lon)) return;
    locationAnimationRef.current = {
      active: true,
      target: latLonToVector3(home.lat, home.lon, 81.5),
      startPos: cameraRef.current.position.clone(),
      startZoom: currentZoomRef.current,
      progress: 0,
    };
    hasAutoFocusedRef.current = true;
  }, [markers]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000814);
    sceneRef.current = scene;

    // Stars with parallax effect
    const starsGeo = new THREE.BufferGeometry();
    const starsMat = new THREE.PointsMaterial({
      size: 1.5,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.9,
    });
    const vertices: number[] = [];
    const colors: number[] = [];
    const sizes: number[] = [];

    for (let i = 0; i < 25000; i++) {
      // Create more stars at greater distances for depth
      const distance = 800 + Math.random() * 2200;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);

      vertices.push(
        distance * Math.sin(phi) * Math.cos(theta),
        distance * Math.sin(phi) * Math.sin(theta),
        distance * Math.cos(phi)
      );

      // More varied star colors
      const colorType = Math.random();
      let c: number[];
      if (colorType < 0.5) {
        c = [1, 1, 1]; // White
      } else if (colorType < 0.7) {
        c = [0.7, 0.85, 1]; // Blue-white
      } else if (colorType < 0.85) {
        c = [1, 0.95, 0.8]; // Warm white
      } else if (colorType < 0.95) {
        c = [1, 0.8, 0.6]; // Orange
      } else {
        c = [0.6, 0.8, 1]; // Blue
      }
      colors.push(...c);
      sizes.push(0.5 + Math.random() * 2);
    }

    starsGeo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    starsGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const stars = new THREE.Points(starsGeo, starsMat);
    scene.add(stars);
    starsRef.current = stars;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      45,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      5000
    );
    camera.position.set(0, 0, 280);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights — tuned for the photographic Earth texture (matches the landing
    // globe): a strong sun for a real day/night terminator, low ambient so the
    // night side stays dark, and a faint cool rim from behind.
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const sunLight = new THREE.DirectionalLight(0xffffff, 1.8);
    sunLight.castShadow = false;
    scene.add(sunLight);
    sunLightRef.current = sunLight;

    const fillLight = new THREE.HemisphereLight(0x4488ff, 0x002244, 0.4);
    scene.add(fillLight);

    // Faint rim from the far side so the dark limb reads against space.
    const backLight = new THREE.DirectionalLight(0x4488cc, 0.25);
    backLight.position.set(-200, 0, -200);
    scene.add(backLight);

    // Globe - use MeshStandardMaterial for better PBR lighting without spots.
    // Base color is ocean blue so that, with no texture (earth-texture.jpg is
    // empty), the globe reads as Earth — the GeoJSON country outlines below
    // supply the continents, matching the public landing globe.
    const globeGeometry = new THREE.SphereGeometry(80, 64, 64);
    const globeMaterial = new THREE.MeshStandardMaterial({
      color: 0x2563eb,
      emissive: new THREE.Color(0x112233),
      emissiveIntensity: 0.05,
      roughness: 0.9, // High roughness = matte surface, no shiny spots
      metalness: 0.0, // Non-metallic surface
    });
    const globe = new THREE.Mesh(globeGeometry, globeMaterial);
    scene.add(globe);
    globeRef.current = globe;

    // Country outlines (ride on the globe) + soft atmosphere edge-glow, so the
    // cockpit globe matches the pretty public landing globe instead of the
    // former flat "blue ball".
    addCountryBorders(globe, 80.2);
    scene.add(createAtmosphere(83));

    // Real Earth surface (NASA Blue Marble) + night lights for NIGHT mode.
    // The version bump re-runs the view-mode effect so the active mode's
    // color/emissive setup is re-applied once a texture actually arrives.
    applyEarthSurface(globeMaterial, () => setTextureVersion((v) => v + 1));
    loadNightLights((texture) => {
      nightTextureRef.current = texture;
      setTextureVersion((v) => v + 1);
    });

    // Clouds layer — the real cloud-cover map loads in as an alpha map (white
    // clouds opaque, gaps transparent); until then it's a soft veil.
    const cloudsGeometry = new THREE.SphereGeometry(81.5, 64, 64);
    const cloudsMaterial = new THREE.MeshPhongMaterial({
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      side: THREE.DoubleSide,
      color: 0xffffff,
    });
    applyCloudLayer(cloudsMaterial);
    const clouds = new THREE.Mesh(cloudsGeometry, cloudsMaterial);
    globe.add(clouds);
    cloudsRef.current = clouds;

    // Sun indicator on globe surface
    const sunIndicator = new THREE.Mesh(
      new THREE.RingGeometry(3, 4, 32),
      new THREE.MeshBasicMaterial({
        color: 0xffff00,
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
      })
    );
    globe.add(sunIndicator);
    sunIndicatorRef.current = sunIndicator;

    // Visible Sun mesh in space
    const sunGeometry = new THREE.SphereGeometry(15, 32, 32);
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: 0xffdd44,
    });
    const sunMesh = new THREE.Mesh(sunGeometry, sunMaterial);
    scene.add(sunMesh);
    sunMeshRef.current = sunMesh;

    // Sun glow sprite
    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = 256;
    glowCanvas.height = 256;
    const glowCtx = glowCanvas.getContext('2d')!;
    const glowGradient = glowCtx.createRadialGradient(128, 128, 0, 128, 128, 128);
    glowGradient.addColorStop(0, 'rgba(255, 220, 100, 1)');
    glowGradient.addColorStop(0.2, 'rgba(255, 200, 50, 0.8)');
    glowGradient.addColorStop(0.4, 'rgba(255, 150, 50, 0.4)');
    glowGradient.addColorStop(0.7, 'rgba(255, 100, 50, 0.1)');
    glowGradient.addColorStop(1, 'rgba(255, 50, 0, 0)');
    glowCtx.fillStyle = glowGradient;
    glowCtx.fillRect(0, 0, 256, 256);

    const glowTexture = new THREE.CanvasTexture(glowCanvas);
    const glowMaterial = new THREE.SpriteMaterial({
      map: glowTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
    });
    const sunGlow = new THREE.Sprite(glowMaterial);
    sunGlow.scale.set(120, 120, 1);
    scene.add(sunGlow);
    sunGlowRef.current = sunGlow;

    // Lens flare effect (additional glow layers)
    const flareCanvas = document.createElement('canvas');
    flareCanvas.width = 128;
    flareCanvas.height = 128;
    const flareCtx = flareCanvas.getContext('2d')!;
    const flareGradient = flareCtx.createRadialGradient(64, 64, 0, 64, 64, 64);
    flareGradient.addColorStop(0, 'rgba(255, 255, 200, 0.6)');
    flareGradient.addColorStop(0.5, 'rgba(255, 200, 100, 0.2)');
    flareGradient.addColorStop(1, 'rgba(255, 100, 0, 0)');
    flareCtx.fillStyle = flareGradient;
    flareCtx.fillRect(0, 0, 128, 128);
    const flareTexture = new THREE.CanvasTexture(flareCanvas);
    const flareMaterial = new THREE.SpriteMaterial({
      map: flareTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
    });
    const sunFlare = new THREE.Sprite(flareMaterial);
    sunFlare.scale.set(200, 200, 1);
    sunMesh.add(sunFlare);

    // Add markers - flat pulsating circles on globe surface
    // Smaller markers to show city-level precision (not cover entire countries)
    const markerInnerGeometry = new THREE.CircleGeometry(0.4, 32);
    const markerOuterGeometry = new THREE.RingGeometry(0.5, 0.8, 32);
    const markerPulseGeometry = new THREE.RingGeometry(0.9, 1.0, 32);

    markers.forEach((marker) => {
      const pos = latLonToVector3(marker.lat, marker.lon, 80.2);
      // Health (F.3 cockpit pins) takes precedence over subscription status.
      const markerColor = marker.health
        ? marker.health === 'ok'
          ? 0x22ff44
          : marker.health === 'warn'
            ? 0xffd700
            : 0xff4444
        : marker.status === 'active'
          ? 0x22ff44
          : marker.status === 'trial'
            ? 0xffd700
            : 0xff4444;

      // Create a group for the marker - positioned slightly above surface
      const markerGroup = new THREE.Group();
      markerGroup.position.copy(pos);

      // Orient marker to face outward from globe center (perpendicular to surface)
      // This makes the flat circle lay on the globe surface
      const normal = pos.clone().normalize();
      const up = new THREE.Vector3(0, 1, 0);
      const right = new THREE.Vector3().crossVectors(up, normal).normalize();
      const adjustedUp = new THREE.Vector3().crossVectors(normal, right).normalize();

      const rotationMatrix = new THREE.Matrix4();
      rotationMatrix.makeBasis(right, adjustedUp, normal);
      markerGroup.quaternion.setFromRotationMatrix(rotationMatrix);

      // Inner solid circle - flat 2D marker
      const innerCircle = new THREE.Mesh(
        markerInnerGeometry,
        new THREE.MeshBasicMaterial({
          color: markerColor,
          transparent: true,
          opacity: 1.0,
          side: THREE.DoubleSide,
          depthTest: true,
          depthWrite: false,
        })
      );
      markerGroup.add(innerCircle);

      // Outer ring
      const outerRing = new THREE.Mesh(
        markerOuterGeometry,
        new THREE.MeshBasicMaterial({
          color: markerColor,
          transparent: true,
          opacity: 0.7,
          side: THREE.DoubleSide,
          depthTest: true,
          depthWrite: false,
        })
      );
      markerGroup.add(outerRing);

      // Pulsing outer ring
      const pulseRing = new THREE.Mesh(
        markerPulseGeometry,
        new THREE.MeshBasicMaterial({
          color: markerColor,
          transparent: true,
          opacity: 0.5,
          side: THREE.DoubleSide,
          depthTest: true,
          depthWrite: false,
        })
      );
      markerGroup.add(pulseRing);

      (markerGroup as any).userData = {
        type: 'organization',
        data: marker,
        pulseRing: pulseRing,
        outerRing: outerRing,
        innerCircle: innerCircle,
      };

      globe.add(markerGroup);
      markersRef.current.push(markerGroup as any);
    });

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      const now = performance.now();
      const deltaTime = now - lastFrameTimeRef.current;

      // Throttle to ~60 FPS for smoother animation
      if (deltaTime < 16) return;
      lastFrameTimeRef.current = now;

      // Parallax effect for stars based on mouse position
      if (starsRef.current) {
        const targetRotationX = mouseWorldRef.current.y * 0.05;
        const targetRotationY = mouseWorldRef.current.x * 0.05;
        starsRef.current.rotation.x += (targetRotationX - starsRef.current.rotation.x) * 0.02;
        starsRef.current.rotation.y += (targetRotationY - starsRef.current.rotation.y) * 0.02;
        // Subtle twinkling effect
        starsRef.current.rotation.z += 0.0001;
      }

      // Location entry animation
      if (
        locationAnimationRef.current.active &&
        locationAnimationRef.current.target &&
        cameraRef.current
      ) {
        locationAnimationRef.current.progress += 0.02;
        const progress = Math.min(locationAnimationRef.current.progress, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic

        if (locationAnimationRef.current.startPos) {
          // Interpolate camera position
          const startPos = locationAnimationRef.current.startPos;
          const targetPos = locationAnimationRef.current.target
            .clone()
            .normalize()
            .multiplyScalar(150);

          cameraRef.current.position.lerpVectors(startPos, targetPos, easeProgress);
          cameraRef.current.lookAt(0, 0, 0);
          currentZoomRef.current = THREE.MathUtils.lerp(
            locationAnimationRef.current.startZoom,
            150,
            easeProgress
          );
          targetZoomRef.current = 150;
        }

        if (progress >= 1) {
          locationAnimationRef.current.active = false;
        }
      }

      // Auto rotate (use ref to avoid re-render issues)
      if (
        autoRotateRef.current &&
        !isDraggingRef.current &&
        !followSunRef.current &&
        globeRef.current &&
        !locationAnimationRef.current.active
      ) {
        globeRef.current.rotation.y += 0.001;
      }

      // Inertia rotation - matches drag rotation logic with world axes
      if (
        !isDraggingRef.current &&
        !autoRotateRef.current &&
        !followSunRef.current &&
        globeRef.current &&
        !locationAnimationRef.current.active
      ) {
        rotationSpeedRef.current.x *= 0.95;
        rotationSpeedRef.current.y *= 0.95;

        if (Math.abs(rotationSpeedRef.current.x) < 0.0001) rotationSpeedRef.current.x = 0;
        if (Math.abs(rotationSpeedRef.current.y) < 0.0001) rotationSpeedRef.current.y = 0;

        if (rotationSpeedRef.current.x !== 0 || rotationSpeedRef.current.y !== 0) {
          // Use world axes for consistent rotation (same as drag)
          const rotationX = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(0, 1, 0),
            rotationSpeedRef.current.x
          );
          const rotationY = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(1, 0, 0),
            rotationSpeedRef.current.y
          );

          globeRef.current.quaternion.premultiply(rotationX);
          globeRef.current.quaternion.premultiply(rotationY);
        }
      }

      // Smooth zoom (when not in location animation)
      if (!locationAnimationRef.current.active) {
        const zoomDiff = targetZoomRef.current - currentZoomRef.current;
        if (Math.abs(zoomDiff) > 0.1) {
          currentZoomRef.current += zoomDiff * 0.1;
          if (cameraRef.current && !followSunRef.current) {
            const currentLength = cameraRef.current.position.length();
            if (currentLength > 0) {
              cameraRef.current.position.normalize().multiplyScalar(currentZoomRef.current);
            } else {
              cameraRef.current.position.set(0, 0, currentZoomRef.current);
            }
            cameraRef.current.lookAt(0, 0, 0);
          }
        }
      }

      // Cloud rotation
      if (cloudsRef.current) {
        cloudsRef.current.rotation.y += 0.00018;
        cloudsRef.current.rotation.x = Math.sin(Date.now() * 0.0001) * 0.02;
      }

      // Update sun mesh and glow position
      if (sunMeshRef.current && sunGlowRef.current && sunLightRef.current) {
        const sunPos = sunLightRef.current.position.clone();
        sunMeshRef.current.position.copy(sunPos);
        sunGlowRef.current.position.copy(sunPos);

        // Pulse the glow
        const glowPulse = 1 + Math.sin(Date.now() * 0.001) * 0.1;
        sunGlowRef.current.scale.set(120 * glowPulse, 120 * glowPulse, 1);
      }

      // Sun indicator on globe
      if (sunIndicatorRef.current && sunLightRef.current) {
        // Calculate sun position on globe surface
        const sunDir = sunLightRef.current.position.clone().normalize();
        const surfacePos = sunDir.multiplyScalar(82);
        sunIndicatorRef.current.position.copy(surfacePos);
        sunIndicatorRef.current.lookAt(0, 0, 0);
        const pulse = 0.8 + Math.sin(Date.now() * 0.002) * 0.2;
        sunIndicatorRef.current.scale.set(pulse, pulse, 1);
      }

      // Marker pulsating animation
      frameCountRef.current++;
      if (frameCountRef.current % 2 === 0) {
        const time = Date.now() * 0.003;
        markersRef.current.forEach((m, i) => {
          const userData = (m as any).userData;
          if (userData.pulseRing) {
            const phase = i * 0.5;
            // Pulse ring expands and fades
            const pulseScale = 1 + Math.sin(time + phase) * 0.5;
            userData.pulseRing.scale.set(pulseScale, pulseScale, 1);
            const pulseMaterial = userData.pulseRing.material as THREE.MeshBasicMaterial;
            pulseMaterial.opacity = 0.2 + Math.cos(time + phase) * 0.2;

            // Inner circle gentle pulse
            const innerScale = 1 + Math.sin(time * 1.5 + phase) * 0.15;
            userData.innerCircle.scale.set(innerScale, innerScale, 1);

            // Outer ring counter-pulse
            const outerScale = 1 + Math.cos(time + phase) * 0.2;
            userData.outerRing.scale.set(outerScale, outerScale, 1);
          }
        });
      }

      renderer.render(scene, camera);
    };

    animate();

    // Mouse handlers
    const handleMouseMove = (e: MouseEvent) => {
      const rect = containerRef.current!.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      mouseRef.current.set(x, y);

      // Update world mouse position for parallax effect
      mouseWorldRef.current = { x, y };

      if (isDraggingRef.current && globeRef.current && cameraRef.current) {
        const deltaX = e.clientX - previousMouseRef.current.x;
        const deltaY = e.clientY - previousMouseRef.current.y;
        const sensitivity = 0.005;

        if (followSunRef.current) {
          const spherical = new THREE.Spherical();
          spherical.setFromVector3(cameraRef.current.position);
          spherical.theta += deltaX * sensitivity;
          spherical.phi += deltaY * sensitivity;
          spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
          cameraRef.current.position.setFromSpherical(spherical);
          cameraRef.current.lookAt(0, 0, 0);
        } else {
          // Direct screen-space rotation: mouse direction = globe rotation direction
          // Drag right = globe rotates right (around Y axis)
          // Drag down = globe rotates down (around X axis in world space)

          // Simple rotation around world axes for intuitive control
          const rotationX = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(0, 1, 0), // World Y axis for horizontal mouse movement
            deltaX * sensitivity
          );
          const rotationY = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(1, 0, 0), // World X axis for vertical mouse movement
            -deltaY * sensitivity
          );

          // Apply rotations - order: first horizontal, then vertical
          globeRef.current.quaternion.premultiply(rotationX);
          globeRef.current.quaternion.premultiply(rotationY);

          // Store rotation speed for inertia
          rotationSpeedRef.current.x = deltaX * sensitivity;
          rotationSpeedRef.current.y = -deltaY * sensitivity;
        }

        previousMouseRef.current = { x: e.clientX, y: e.clientY };
      } else if (!isDraggingRef.current && globeRef.current) {
        raycasterRef.current.setFromCamera(mouseRef.current, camera);
        const intersects = raycasterRef.current.intersectObjects(globeRef.current.children, true);
        // Find userData by traversing up to parent if needed
        const hit = intersects.find((i) => {
          let obj: THREE.Object3D | null = i.object;
          while (obj) {
            if ((obj as any).userData?.type) return true;
            obj = obj.parent;
          }
          return false;
        });
        if (hit) {
          let obj: THREE.Object3D | null = hit.object;
          while (obj && !(obj as any).userData?.type) {
            obj = obj.parent;
          }
          setHoveredItem(obj ? (obj as any).userData : null);
        } else {
          setHoveredItem(null);
        }
        document.body.style.cursor = hit ? 'pointer' : 'grab';
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDraggingRef.current = true;
      previousMouseRef.current = { x: e.clientX, y: e.clientY };
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      document.body.style.cursor = 'grabbing';
      setAutoRotate(false);
      rotationSpeedRef.current = { x: 0, y: 0 };
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = 'grab';

      const deltaX = e.clientX - previousMouseRef.current.x;
      const deltaY = e.clientY - previousMouseRef.current.y;
      const totalDragDistance =
        Math.abs(e.clientX - dragStartRef.current.x) + Math.abs(e.clientY - dragStartRef.current.y);

      if (!followSunRef.current && totalDragDistance > 5) {
        // Match the direction used in drag handling
        rotationSpeedRef.current.x = deltaX * 0.001;
        rotationSpeedRef.current.y = -deltaY * 0.001;
      }
    };

    const handleClick = (e: MouseEvent) => {
      const clickDistance =
        Math.abs(e.clientX - dragStartRef.current.x) + Math.abs(e.clientY - dragStartRef.current.y);
      if (clickDistance < 5 && globeRef.current && cameraRef.current) {
        raycasterRef.current.setFromCamera(mouseRef.current, camera);
        const intersects = raycasterRef.current.intersectObjects(globeRef.current.children, true);
        // Find userData by traversing up to parent if needed
        const hit = intersects.find((i) => {
          let obj: THREE.Object3D | null = i.object;
          while (obj) {
            if ((obj as any).userData?.type) return true;
            obj = obj.parent;
          }
          return false;
        });
        if (hit) {
          // Traverse up to find the parent with userData
          let obj: THREE.Object3D | null = hit.object;
          while (obj && !(obj as any).userData?.type) {
            obj = obj.parent;
          }
          const hitData = obj ? (obj as any).userData : null;
          if (!hitData) return;
          setSelectedItem(hitData);

          // Start location entry animation
          if (hitData.data && hitData.data.lat !== undefined && hitData.data.lon !== undefined) {
            const targetPos = latLonToVector3(hitData.data.lat, hitData.data.lon, 81.5);
            locationAnimationRef.current = {
              active: true,
              target: targetPos,
              startPos: cameraRef.current.position.clone(),
              startZoom: currentZoomRef.current,
              progress: 0,
            };
          }
        }
      }
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomDelta = e.deltaY > 0 ? -10 : 10;
      targetZoomRef.current = Math.max(120, Math.min(400, targetZoomRef.current + zoomDelta));
    };

    const handleResize = () => {
      camera.aspect = containerRef.current!.clientWidth / containerRef.current!.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current!.clientWidth, containerRef.current!.clientHeight);
    };

    // Helper function to calculate distance between two touch points
    const getTouchDistance = (touch1: Touch, touch2: Touch): number => {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Touch handlers for mobile devices
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();

      if (e.touches.length === 2) {
        // Start pinch-to-zoom
        isPinchingRef.current = true;
        isDraggingRef.current = false;
        initialPinchDistanceRef.current = getTouchDistance(e.touches[0], e.touches[1]);
      } else if (e.touches.length === 1) {
        // Single touch - start rotation
        isDraggingRef.current = true;
        isPinchingRef.current = false;
        const touch = e.touches[0];
        previousMouseRef.current = { x: touch.clientX, y: touch.clientY };
        dragStartRef.current = { x: touch.clientX, y: touch.clientY };
        setAutoRotate(false);
        rotationSpeedRef.current = { x: 0, y: 0 };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();

      if (e.touches.length === 2 && isPinchingRef.current) {
        // Pinch-to-zoom
        const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / initialPinchDistanceRef.current;

        // Zoom based on pinch scale (invert: pinch in = zoom out, pinch out = zoom in)
        const zoomSensitivity = 120;
        const zoomDelta = (1 - scale) * zoomSensitivity;
        targetZoomRef.current = Math.max(120, Math.min(400, currentZoomRef.current + zoomDelta));

        // Update initial distance for continuous pinching
        initialPinchDistanceRef.current = currentDistance;
      } else if (
        e.touches.length === 1 &&
        isDraggingRef.current &&
        globeRef.current &&
        cameraRef.current
      ) {
        // Single touch - globe rotation
        const touch = e.touches[0];
        const deltaX = touch.clientX - previousMouseRef.current.x;
        const deltaY = touch.clientY - previousMouseRef.current.y;
        const sensitivity = 0.005;

        if (followSunRef.current) {
          const spherical = new THREE.Spherical();
          spherical.setFromVector3(cameraRef.current.position);
          spherical.theta += deltaX * sensitivity;
          spherical.phi += deltaY * sensitivity;
          spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
          cameraRef.current.position.setFromSpherical(spherical);
          cameraRef.current.lookAt(0, 0, 0);
        } else {
          // Direct screen-space rotation: mouse direction = globe rotation direction
          const rotationX = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(0, 1, 0),
            deltaX * sensitivity
          );
          const rotationY = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(1, 0, 0),
            -deltaY * sensitivity
          );

          globeRef.current.quaternion.premultiply(rotationX);
          globeRef.current.quaternion.premultiply(rotationY);

          rotationSpeedRef.current.x = deltaX * sensitivity;
          rotationSpeedRef.current.y = -deltaY * sensitivity;
        }

        previousMouseRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // If still have touches, might be transitioning from pinch to single touch
      if (e.touches.length === 1) {
        isPinchingRef.current = false;
        isDraggingRef.current = true;
        const touch = e.touches[0];
        previousMouseRef.current = { x: touch.clientX, y: touch.clientY };
        dragStartRef.current = { x: touch.clientX, y: touch.clientY };
        return;
      }

      if (e.touches.length === 0) {
        const wasDragging = isDraggingRef.current;
        const wasPinching = isPinchingRef.current;
        isDraggingRef.current = false;
        isPinchingRef.current = false;

        // Don't check for tap if we were pinching
        if (wasPinching) return;

        const touch = e.changedTouches[0];
        const totalDragDistance =
          Math.abs(touch.clientX - dragStartRef.current.x) +
          Math.abs(touch.clientY - dragStartRef.current.y);

        // Check for tap (small movement)
        if (wasDragging && totalDragDistance < 10 && globeRef.current && cameraRef.current) {
          const rect = containerRef.current!.getBoundingClientRect();
          const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
          const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
          mouseRef.current.set(x, y);

          raycasterRef.current.setFromCamera(mouseRef.current, camera);
          const intersects = raycasterRef.current.intersectObjects(globeRef.current.children, true);

          const hit = intersects.find((i) => {
            let obj: THREE.Object3D | null = i.object;
            while (obj) {
              if ((obj as any).userData?.type) return true;
              obj = obj.parent;
            }
            return false;
          });

          if (hit) {
            let obj: THREE.Object3D | null = hit.object;
            while (obj && !(obj as any).userData?.type) {
              obj = obj.parent;
            }
            const hitData = obj ? (obj as any).userData : null;
            if (hitData) {
              setSelectedItem(hitData);

              if (
                hitData.data &&
                hitData.data.lat !== undefined &&
                hitData.data.lon !== undefined
              ) {
                const targetPos = latLonToVector3(hitData.data.lat, hitData.data.lon, 81.5);
                locationAnimationRef.current = {
                  active: true,
                  target: targetPos,
                  startPos: cameraRef.current.position.clone(),
                  startZoom: currentZoomRef.current,
                  progress: 0,
                };
              }
            }
          }
        } else if (!followSunRef.current && totalDragDistance > 5) {
          const deltaX = touch.clientX - previousMouseRef.current.x;
          const deltaY = touch.clientY - previousMouseRef.current.y;
          rotationSpeedRef.current.x = deltaX * 0.001;
          rotationSpeedRef.current.y = -deltaY * 0.001;
        }
      }
    };

    renderer.domElement.addEventListener('mousemove', handleMouseMove);
    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    renderer.domElement.addEventListener('mouseup', handleMouseUp);
    renderer.domElement.addEventListener('mouseleave', handleMouseUp);
    renderer.domElement.addEventListener('click', handleClick);
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });
    renderer.domElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    renderer.domElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    renderer.domElement.addEventListener('touchend', handleTouchEnd, { passive: false });
    window.addEventListener('resize', handleResize);

    return () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      renderer.domElement.removeEventListener('mouseup', handleMouseUp);
      renderer.domElement.removeEventListener('mouseleave', handleMouseUp);
      renderer.domElement.removeEventListener('click', handleClick);
      renderer.domElement.removeEventListener('wheel', handleWheel);
      renderer.domElement.removeEventListener('touchstart', handleTouchStart);
      renderer.domElement.removeEventListener('touchmove', handleTouchMove);
      renderer.domElement.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [markers]);

  // Update view mode effects
  useEffect(() => {
    if (!globeRef.current?.material || !cloudsRef.current?.material) return;

    const material = globeRef.current.material as THREE.MeshPhongMaterial;
    const cloudsMaterial = cloudsRef.current.material as THREE.MeshPhongMaterial;

    setTransitionOpacity(0);

    switch (viewMode) {
      case VIEW_MODES.WINTER:
        material.emissiveMap = null;
        material.color = new THREE.Color(0.85, 0.9, 1.0);
        material.emissive = new THREE.Color(0x223366);
        material.emissiveIntensity = 0.15;
        material.shininess = 50;
        cloudsMaterial.opacity = 0.5;
        break;

      case VIEW_MODES.INFRARED:
        material.emissiveMap = null;
        material.color = new THREE.Color(1.0, 0.3, 0.1);
        material.emissive = new THREE.Color(0xff2200);
        material.emissiveIntensity = 0.4;
        material.shininess = 10;
        cloudsMaterial.opacity = 0.2;
        break;

      case VIEW_MODES.NIGHT:
        // Real city lights when the night map is in; else the old amber glow.
        if (nightTextureRef.current) {
          material.emissiveMap = nightTextureRef.current;
          material.color = new THREE.Color(0x10131f); // darken day surface
          material.emissive = new THREE.Color(0xffddaa);
          material.emissiveIntensity = 1.1;
        } else {
          material.emissiveMap = null;
          material.color = new THREE.Color(0.1, 0.1, 0.15);
          material.emissive = new THREE.Color(0xffaa00);
          material.emissiveIntensity = 0.6;
        }
        material.shininess = 5;
        cloudsMaterial.opacity = 0.08;
        break;

      case VIEW_MODES.NASA_LIVE:
      case VIEW_MODES.REALISTIC:
      case VIEW_MODES.DAY:
      default:
        // With a texture, white lets it show true colors; without one, stay
        // ocean blue so the globe doesn't wash out to a pale grey ball.
        material.emissiveMap = null;
        material.color = new THREE.Color(material.map ? 0xffffff : 0x2563eb);
        material.emissive = new THREE.Color(0x112244);
        material.emissiveIntensity = 0.05;
        material.shininess = 30;
        cloudsMaterial.opacity = 0.3;
        break;
    }

    material.needsUpdate = true;
    cloudsMaterial.needsUpdate = true;

    setTimeout(() => setTransitionOpacity(1), 100);
    // textureVersion re-applies the active mode once a texture actually loads.
  }, [viewMode, textureVersion]);

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="h-full min-h-0 w-full bg-gradient-to-br from-black via-gray-950 to-indigo-950 flex overflow-hidden relative rounded-ids-lg">
      <div ref={containerRef} className="w-full h-full absolute inset-0" />
      <div
        className="absolute inset-0 pointer-events-none bg-black transition-opacity duration-300"
        style={{ opacity: 1 - transitionOpacity }}
      />

      {/* Hover Tooltip */}
      {hoveredItem && (
        <div className="absolute top-4 right-4 bg-gray-900/95 backdrop-blur-sm border border-blue-500/30 rounded-ids-md p-4 max-w-xs shadow-2xl z-10">
          <h3 className="font-semibold text-white mb-2">{hoveredItem.type}</h3>
          <p className="text-sm text-gray-300 font-medium">{hoveredItem.data.name}</p>
          <p className="text-xs text-gray-400 mt-1">Click for details</p>
        </div>
      )}

      {/* Selected Item Modal */}
      {selectedItem && (
        <div className="absolute bottom-4 right-4 bg-gray-900/95 backdrop-blur-sm border border-blue-500/30 rounded-ids-md p-6 max-w-md shadow-2xl z-10">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">{selectedItem.data.name}</h2>
              <p className="text-sm text-blue-400">{selectedItem.type}</p>
            </div>
            <button
              onClick={() => setSelectedItem(null)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded">
              <span className="text-sm text-gray-400">Status</span>
              <span
                className={`text-lg font-semibold capitalize ${
                  selectedItem.data.status === 'active'
                    ? 'text-green-400'
                    : selectedItem.data.status === 'trial'
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`}
              >
                {selectedItem.data.status}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded">
              <span className="text-sm text-gray-400">Plan</span>
              <span className="text-lg font-semibold text-white capitalize">
                {selectedItem.data.plan}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer with UTC time */}
      <div className="absolute bottom-4 left-4 flex items-center gap-3 z-10">
        <div className="flex items-center gap-2 bg-gray-900/80 backdrop-blur-sm border border-amber-500/30 rounded-ids-md px-3 py-1.5">
          <Sun size={14} className="text-amber-400" />
          <span className="text-amber-300 text-xs font-mono">UTC {formatTime(currentTime)}</span>
        </div>
      </div>

      {/* Bottom center hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 bg-gray-900/60 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
          <Globe size={14} className="text-cyan-400" />
          <span className="text-cyan-300/70 text-xs">
            {t('dashboard.clickLocationHint', 'Kliknij na lokalizację, aby zobaczyć szczegóły')}
          </span>
        </div>
      </div>

      {/* Location count badge */}
      <div className="absolute bottom-4 right-4 z-10">
        <div className="flex items-center gap-2 bg-gradient-to-r from-green-600/80 to-emerald-600/80 backdrop-blur-sm border border-green-400/30 rounded-ids-md px-3 py-2">
          <MapPin size={16} className="text-green-300" />
          <div className="flex flex-col">
            <span className="text-white text-lg font-bold leading-none">{markers.length}</span>
            <span className="text-green-200 text-[10px]">
              {t('dashboard.activeLocations', 'Aktywne lokalizacje')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EarthDashboard;
